import React, {createContext, useEffect, useState} from 'react'
import './App.css'
import HashChecker from './models/HashChecker'
import Authenticator from './models/Authenticator'
import Fetcher from './models/Fetcher'
import StoryContainer from './components/StoryContainer'
import Story from './components/Story'
import PasswordForm from './components/PasswordForm'
import NotFound from './components/NotFound'
import Loading from './components/Loading'
import './models/Rollbar'
import dayjs from 'dayjs'

type Props = {
  match: {
    params: {
      hash: string
    }
  }
}

// TODO(kyokutyo): 適当な関数 どうにかする
const _convert = (story: responseStory): story => {
  return {
    info: {
      ...story?.info,
      start: dayjs(story?.info?.start).isValid() ? dayjs(story?.info?.start) : dayjs(0),
      end: dayjs(story?.info?.end).isValid() ? dayjs(story?.info?.end) : dayjs(0),
      createdAt: dayjs(story?.info?.createdAt).isValid() ? dayjs(story?.info?.createdAt) : dayjs(0),
    },
    contents: story?.contents
      .sort((a, b) => a?.order - b?.order)
      .map((content: responseStoryContent): storyContent => {
        return {
          image: content?.image,
          comment: content?.comment,
          date: dayjs(content?.date).isValid() ? dayjs(content?.date) : dayjs(0),
        }
      })
  }
}

const _updateHead = (info: responseStoryInfo) => {
  const titleEl = document.getElementsByTagName('title')[0]
  titleEl.innerText = `${info.title} | ノハナ（nohana）`
}

export const AppContext = createContext({} as {
  app: {
    password: string,
    hash: string,
  },
  setApp: React.Dispatch<React.SetStateAction<{ password: string, hash: string }>>
})

export default function App(props: Props) {
  const [app, setApp] = useState({
    password: '',
    hash: props.match.params.hash,
  })
  const [isValidHash, setIsValidHash] = useState(false)
  const [token, setToken] = useState('' as string)
  const [story, setStory] = useState({} as story)
  const [state, setState] = useState('beforeFetch' as state)
  const [errors, setErrors] = useState([] as string[])

  // ハッシュの存在チェック、パスワードの要不要を確認
  useEffect(() => {
    if (app?.hash === '404') {
      setState('notFound')
      return
    }
    const checker = new HashChecker(app?.hash)
    checker.check().then(res => {
      if (!res) {
        setState('unknownStatus')
        return
      }
      setIsValidHash(res?.status === 200)
    }).catch(err => {
      setIsValidHash(false)
      switch (err?.response?.status) {
        case 401:
        case 404:
          setState('notFound')
          break
        case 503:
          setState('storySharingUnavailable')
          break
        case 500:
        default:
          setState('unknownStatus')
          break
      }
    })
  }, [app?.hash])

  // 正しいハッシュであることが確認できた場合、 JWT 取得
  useEffect(() => {
    const ngStates: state[] = ['notFound', 'storySharingUnavailable', 'unknownStatus']
    if (!isValidHash || ngStates.includes(state)) {
      return
    }
    const authenticator = new Authenticator(app?.hash, app?.password)
    authenticator.auth().then(res => {
      if (!res) {
        setState('unknownStatus')
        return
      }
      setState('authorizeSucceeded')
      setToken(res?.data?.token)
    }).catch(err => {
      switch (err?.response?.status) {
        case 401:
          if (app?.password !== '') {
            setErrors(['あいことばが違います'])
            setState('authorizeFailed')
          } else {
            setState('needAuthorize')
          }
          break
        case 404:
          setState('notFound')
          break
        case 503:
          setState('storySharingUnavailable')
          break
        case 500:
        default:
          setState('unknownStatus')
          break
      }
    })
    // eslint-disable-next-line
  }, [isValidHash, app?.password])

  // JWT 取得できたらコンテンツの取得
  useEffect(() => {
    const ngStates: state[] = ['notFound', 'storySharingUnavailable', 'unknownStatus', 'authorizeFailed', 'needAuthorize']
    if (!isValidHash || token === '' || ngStates.includes(state)) {
      return
    }
    const fetcher = new Fetcher(token)
    fetcher.fetch().then(res => {
      if (!res) {
        setState('unknownStatus')
        return
      }
      setStory(_convert(res))
      setState('fetchSucceeded')
      _updateHead(res?.info)
    }).catch(err => {
      switch (err?.response?.status) {
        case 401:
          setState('authorizeFailed')
          break
        case 404:
          setState('notFound')
          break
        case 503:
          setState('storySharingUnavailable')
          break
        case 500:
        default:
          setState('unknownStatus')
          break
      }
    })
    // eslint-disable-next-line
  }, [isValidHash, token])

  switch (state) {
    case 'fetchSucceeded':
      return (
        <AppContext.Provider value={{ app, setApp }}>
          <StoryContainer>
            <Story story={story} />
          </StoryContainer>
        </AppContext.Provider>
      )
    case 'authorizeFailed':
    case 'needAuthorize':
      return (
        <AppContext.Provider value={{ app, setApp }}>
          <StoryContainer classes={['password-form']}>
            <PasswordForm errors={errors} />
          </StoryContainer>
        </AppContext.Provider>
      )
    case 'notFound':
    case 'storySharingUnavailable':
    case 'unknownStatus':
      return (
        <AppContext.Provider value={{ app, setApp }}>
          <StoryContainer classes={['not-found']}>
            <NotFound />
          </StoryContainer>
        </AppContext.Provider>
      )
    case 'beforeFetch':
    case 'authorizeSucceeded':
    default:
      return (
        <AppContext.Provider value={{ app, setApp }}>
          <StoryContainer classes={['loading']}>
            <Loading />
          </StoryContainer>
        </AppContext.Provider>
      )
  }
}
